import React from 'react'
import PropTypes from 'prop-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMediaQuery } from '@mui/material'
import { useNavigate, useParams, Navigate } from 'react-router-dom'
import { PageLayout, PageHeader } from '@components'
import useAuthz from '../../hooks/useAuthz'
import { getVisibleSettingsItems } from './settingsMenuItems'
import styles from './Settings.module.scss'

const SettingsPage = ({ userID, userOrganization, user }) => {
  const { isAdmin } = useAuthz()
  const { tab } = useParams()
  const navigate = useNavigate()
  const isMobile = useMediaQuery('(max-width: 768px)')

  const visibleMenuItems = getVisibleSettingsItems(isAdmin)

  // No tab is visible to this user, so leave Settings instead of redirecting to an undefined slug
  if (visibleMenuItems.length === 0) {
    return <Navigate to='/' replace />
  }

  const defaultSlug = visibleMenuItems[0].slug

  // If no tab param, redirect to default tab
  if (!tab) {
    return <Navigate to={`/settings/${defaultSlug}`} replace />
  }

  const activeIndex = visibleMenuItems.findIndex((item) => item.slug === tab)
  const selectedIndex = activeIndex >= 0 ? activeIndex : 0

  // If tab slug is invalid, redirect to default
  if (activeIndex < 0) {
    return <Navigate to={`/settings/${defaultSlug}`} replace />
  }

  const handleTabClick = (index) => {
    if (visibleMenuItems[index]?.disabled) return
    navigate(`/settings/${visibleMenuItems[index].slug}`)
  }

  const SelectedComponent =
    visibleMenuItems[selectedIndex]?.component || visibleMenuItems[0]?.component

  return (
    <PageLayout>
      <PageHeader title='Settings' subtitle='Manage your workspace preferences and integrations' />

      {/* Horizontal Tab Bar */}
      <div className={styles.tabBar}>
        <div className={styles.tabScroller}>
          {visibleMenuItems.map((item, index) => (
            <button
              key={item.slug}
              className={`${styles.tab} ${selectedIndex === index ? styles.tabActive : ''}`}
              onClick={() => handleTabClick(index)}
              disabled={item.disabled}
              type='button'
            >
              <FontAwesomeIcon icon={item.icon} className={styles.tabIcon} />
              {!isMobile && (
                <span className={styles.tabLabel} data-text={item.name}>
                  {item.name}
                </span>
              )}
              {isMobile && <span className={styles.tabLabelMobile}>{item.shortName}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Content Area */}
      <div className={styles.contentArea}>
        <SelectedComponent userID={userID} userOrganization={userOrganization} user={user} />
      </div>
    </PageLayout>
  )
}

SettingsPage.propTypes = {
  userID: PropTypes.string,
  userOrganization: PropTypes.string,
  user: PropTypes.object
}

export default SettingsPage
